// src/components/onboarding/PerformanceDropsAnalysis.tsx
import React, { useMemo, useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn, formatNumber } from "@/lib/utils";
import { QuickWinCard } from "./QuickWinCard";
import { CopyButton } from "@/components/ui/copy-button";
import type { OnPerformanceData } from "@/types/onboarding";

// Configuration constants
const DEFAULT_VISIBLE_CAMPAIGNS = 3; // Number of campaigns to show by default

interface PerformanceDropsAnalysisProps {
  data: OnPerformanceData[] | undefined;
  minImpressions: string;
  minConversions: string;
  onMinImpressionsChange: (value: string) => void;
  onMinConversionsChange: (value: string) => void;
  onCountChange?: (count: number) => void;
}

export const PerformanceDropsAnalysis: React.FC<
  PerformanceDropsAnalysisProps
> = ({
  data,
  minImpressions,
  minConversions,
  onMinImpressionsChange,
  onMinConversionsChange,
  onCountChange,
}) => {
  const [timePeriod, setTimePeriod] = useState("13w");
  const [showAllCampaigns, setShowAllCampaigns] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [sortBy, setSortBy] = useState("conversions-percentage");

  // Enhanced Performance Data Analysis
  const performanceInsights = useMemo(() => {
    const sourceData = (data || []) as OnPerformanceData[];
    if (!sourceData.length) return { campaigns: [], summary: null };

    const impressionThreshold = parseInt(minImpressions, 10) || 0;
    const conversionThreshold = parseInt(minConversions, 10) || 0;

    // Determine which fields to use based on time period
    const isWeek13 = timePeriod === "13w";
    const impressionAvgField = isWeek13 ? "ImprAvg13W" : "ImprAvg4W";
    const conversionAvgField = isWeek13 ? "ConvAvg13W" : "ConvAvg4W";
    const costAvgField = isWeek13 ? "CostAvg13W" : "CostAvg4W";
    const impressionDropField = isWeek13 ? "ImprDrop13W" : "ImprDrop4W";
    const conversionDropField = isWeek13 ? "ConvDrop13W" : "ConvDrop4W";
    const costDropField = isWeek13 ? "CostDrop13W" : "CostDrop4W";

    const filteredCampaigns = sourceData
      .filter((item) => {
        // Handle field access more carefully - some 4W fields might be undefined/empty
        const avgImpressions = Number(item[impressionAvgField]) || 0;
        const avgConversions = Number(item[conversionAvgField]) || 0;

        const meetsThresholds =
          avgImpressions >= impressionThreshold &&
          avgConversions >= conversionThreshold;

        // Handle drop fields - they should be numbers, but handle undefined/null gracefully
        const impressionDrop =
          typeof item[impressionDropField] === "number"
            ? item[impressionDropField]
            : 0;
        const conversionDrop =
          typeof item[conversionDropField] === "number"
            ? item[conversionDropField]
            : 0;
        const costDrop =
          typeof item[costDropField] === "number" ? item[costDropField] : 0;

        // More nuanced drop detection
        const hasSignificantDrop =
          impressionDrop > 20 || // 20% impression drop
          conversionDrop > 15 || // 15% conversion drop
          costDrop > 25; // 25% cost drop (spend drops are less critical)

        return meetsThresholds && hasSignificantDrop;
      })
      .sort((a, b) => {
        const aConvDrop = a[conversionDropField] || 0;
        const bConvDrop = b[conversionDropField] || 0;
        const aImprDrop = a[impressionDropField] || 0;
        const bImprDrop = b[impressionDropField] || 0;
        const aCostDrop = a[costDropField] || 0;
        const bCostDrop = b[costDropField] || 0;

        // Calculate absolute drops
        const aConvAbsolute = (a[conversionAvgField] || 0) * (aConvDrop / 100);
        const bConvAbsolute = (b[conversionAvgField] || 0) * (bConvDrop / 100);
        const aImprAbsolute = (a[impressionAvgField] || 0) * (aImprDrop / 100);
        const bImprAbsolute = (b[impressionAvgField] || 0) * (bImprDrop / 100);
        const aCostAbsolute = (a[costAvgField] || 0) * (aCostDrop / 100);
        const bCostAbsolute = (b[costAvgField] || 0) * (bCostDrop / 100);

        switch (sortBy) {
          case "impressions-percentage":
            return bImprDrop - aImprDrop;
          case "impressions-absolute":
            return bImprAbsolute - aImprAbsolute;
          case "conversions-percentage":
            return bConvDrop - aConvDrop;
          case "conversions-absolute":
            return bConvAbsolute - aConvAbsolute;
          case "spend-percentage":
            return bCostDrop - aCostDrop;
          case "spend-absolute":
            return bCostAbsolute - aCostAbsolute;
          default:
            // Default: Weight conversion drops more heavily
            const aScore = aConvDrop * 1.5 + aImprDrop + aCostDrop * 0.5;
            const bScore = bConvDrop * 1.5 + bImprDrop + aCostDrop * 0.5;
            return bScore - aScore;
        }
      });

    const totalAffectedSpend = filteredCampaigns.reduce((sum, camp) => {
      return sum + (camp[costAvgField] || 0);
    }, 0);

    const worstCampaign = filteredCampaigns[0];

    return {
      campaigns: filteredCampaigns,
      summary: {
        count: filteredCampaigns.length,
        totalSpend: totalAffectedSpend,
        worstCampaign: worstCampaign
          ? {
              name: worstCampaign.Campaign,
              worstMetric: Math.max(
                worstCampaign[conversionDropField] || 0,
                worstCampaign[impressionDropField] || 0,
                worstCampaign[costDropField] || 0,
              ),
            }
          : null,
      },
      timePeriod,
      fieldMappings: {
        impressionAvgField,
        conversionAvgField,
        costAvgField,
        impressionDropField,
        conversionDropField,
        costDropField,
      },
    };
  }, [data, minImpressions, minConversions, timePeriod, sortBy]);

  // Notify parent component of count changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(performanceInsights.campaigns.length);
    }
  }, [performanceInsights.campaigns.length, onCountChange]);

  return (
    <QuickWinCard
      title="Performance Drops"
      description="Campaigns with significant performance declines compared to historical averages"
      icon={<AlertTriangle />}
      severity={performanceInsights.campaigns.length > 0 ? "high" : "good"}
    >
      <div className="space-y-4">
        {performanceInsights.campaigns.length > 0 ? (
          <>
            {/* Main Alert with Copy Button */}
            <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-red-800">
                      {performanceInsights.campaigns.length} campaigns showing
                      significant performance drops
                    </h4>
                    <p className="text-red-700 text-sm mt-1">
                      Combined weekly spend:{" "}
                      <strong>
                        $
                        {formatNumber(
                          performanceInsights.summary?.totalSpend || 0,
                          0,
                        )}
                      </strong>
                    </p>
                    {performanceInsights.summary?.worstCampaign && (
                      <p className="text-red-700 text-sm mt-1">
                        Worst performer:{" "}
                        <strong>
                          {performanceInsights.summary.worstCampaign.name}
                        </strong>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 ml-4">
                  <CopyButton
                    text={performanceInsights.campaigns
                      .map(
                        (campaign: any) =>
                          `${campaign.Campaign}\t${campaign.CampaignId || "N/A"}`,
                      )
                      .join("\n")}
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                  >
                    Copy All Campaigns
                  </CopyButton>
                  <div className="text-xs text-red-600 bg-red-100 px-3 py-2 rounded min-w-fit">
                    <strong>Note:</strong> Filters applied below.
                    <br />
                    Adjust thresholds to see different results.
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign Details - Always Expanded */}
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium text-slate-700 min-w-fit">
                      Compare vs:
                    </Label>
                    <Select value={timePeriod} onValueChange={setTimePeriod}>
                      <SelectTrigger className="h-8 bg-white border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4w">4 week ave</SelectItem>
                        <SelectItem value="13w">13 week ave</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500 hidden xl:inline">
                      (stable trends)
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium text-slate-700 min-w-fit">
                      Min conversions:
                    </Label>
                    <Select
                      value={minConversions}
                      onValueChange={onMinConversionsChange}
                    >
                      <SelectTrigger className="w-20 h-8 bg-white border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["0", "1", "3", "5", "10", "20"].map((val) => (
                          <SelectItem key={val} value={val}>
                            {val}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500 hidden xl:inline">
                      /week
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium text-slate-700 min-w-fit">
                      Min impressions:
                    </Label>
                    <Select
                      value={minImpressions}
                      onValueChange={onMinImpressionsChange}
                    >
                      <SelectTrigger className="w-24 h-8 bg-white border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["0", "100", "500", "1000", "5000", "10000"].map(
                          (val) => (
                            <SelectItem key={val} value={val}>
                              {val}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500 hidden xl:inline">
                      /week
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium text-slate-700 min-w-fit">
                      Sort by:
                    </Label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-8 bg-white border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="impressions-percentage">
                          Impr Drop %
                        </SelectItem>
                        <SelectItem value="impressions-absolute">
                          Impr Drop #
                        </SelectItem>
                        <SelectItem value="spend-percentage">
                          Spend Drop %
                        </SelectItem>
                        <SelectItem value="spend-absolute">
                          Spend Drop $
                        </SelectItem>
                        <SelectItem value="conversions-percentage">
                          Conv Drop %
                        </SelectItem>
                        <SelectItem value="conversions-absolute">
                          Conv Drop #
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Comprehensive Table Header */}
              <div className="overflow-x-auto">
                <div className="min-w-[1400px] space-y-2">
                  <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-100 rounded text-xs font-medium text-slate-700">
                    <div className="col-span-3">
                      Campaign
                      <div className="text-xs font-normal text-slate-500">
                        Name & Campaign ID
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      Impressions
                      <div className="text-xs font-normal text-slate-500">
                        Drop % | Current
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      Clicks
                      <div className="text-xs font-normal text-slate-500">
                        Drop % | Current
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      Conversions
                      <div className="text-xs font-normal text-slate-500">
                        Drop % | Current
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      CPA
                      <div className="text-xs font-normal text-slate-500">
                        Current Cost/Conv
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      Weekly Spend
                      <div className="text-xs font-normal text-slate-500">
                        Current Amount
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      CTR
                      <div className="text-xs font-normal text-slate-500">
                        Current Rate
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      Conv Rate
                      <div className="text-xs font-normal text-slate-500">
                        Current Rate
                      </div>
                    </div>
                    <div className="col-span-2 text-center">
                      Risk Level & Actions
                      <div className="text-xs font-normal text-slate-500">
                        Severity & Copy Data
                      </div>
                    </div>
                  </div>

                  {/* Campaign Rows */}
                  <div className="space-y-1">
                    {performanceInsights.campaigns
                      .slice(
                        0,
                        showAllCampaigns
                          ? performanceInsights.campaigns.length
                          : DEFAULT_VISIBLE_CAMPAIGNS,
                      )
                      .map((campaign, index) => {
                        const { fieldMappings } = performanceInsights;

                        // Get values using dynamic field names with proper type handling
                        const impressionDrop =
                          typeof campaign[fieldMappings.impressionDropField] ===
                          "number"
                            ? campaign[fieldMappings.impressionDropField]
                            : 0;
                        const conversionDrop =
                          typeof campaign[fieldMappings.conversionDropField] ===
                          "number"
                            ? campaign[fieldMappings.conversionDropField]
                            : 0;
                        const costDrop =
                          typeof campaign[fieldMappings.costDropField] ===
                          "number"
                            ? campaign[fieldMappings.costDropField]
                            : 0;
                        const impressionAvg =
                          Number(campaign[fieldMappings.impressionAvgField]) ||
                          0;
                        const conversionAvg =
                          Number(campaign[fieldMappings.conversionAvgField]) ||
                          0;
                        const costAvg =
                          Number(campaign[fieldMappings.costAvgField]) || 0;

                        // Improved severity calculation
                        const worstDrop = Math.max(
                          conversionDrop,
                          impressionDrop,
                          costDrop * 0.7,
                        ); // Cost drops weighted lower
                        const severity =
                          worstDrop >= 70
                            ? "critical"
                            : worstDrop >= 40
                              ? "high"
                              : "medium";

                        const severityConfig = {
                          critical: {
                            bg: "bg-red-100",
                            border: "border-l-red-600",
                            badge: "destructive",
                            label: "Critical",
                          },
                          high: {
                            bg: "bg-orange-100",
                            border: "border-l-orange-500",
                            badge: "secondary",
                            label: "High",
                          },
                          medium: {
                            bg: "bg-yellow-100",
                            border: "border-l-yellow-500",
                            badge: "outline",
                            label: "Medium",
                          },
                        };

                        const config = severityConfig[severity];

                        return (
                          <div
                            key={`${campaign.Campaign}-${index}`}
                            className={cn(
                              "group grid grid-cols-12 gap-3 px-4 py-3 border-l-2 rounded-r text-sm hover:shadow-sm transition-all",
                              config.bg,
                              config.border,
                            )}
                          >
                            {/* Campaign Name */}
                            <div className="col-span-4 font-medium text-slate-800">
                              <div className="flex items-center gap-2">
                                <div
                                  className="truncate flex-1"
                                  title={campaign.Campaign}
                                >
                                  {campaign.Campaign}
                                </div>
                                <CopyButton
                                  text={`${campaign.Campaign}\t${campaign.CampaignId || "N/A"}`}
                                  size="sm"
                                  variant="ghost"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                />
                              </div>
                            </div>

                            {/* Impressions */}
                            <div className="col-span-2 text-center">
                              {impressionDrop > 0 ? (
                                <div>
                                  <div className="font-semibold text-red-700 flex items-center justify-center gap-1">
                                    <span>{impressionDrop.toFixed(0)}%</span>
                                    <span className="text-red-600">|</span>
                                    <span>
                                      {formatNumber(
                                        impressionAvg * (impressionDrop / 100),
                                        0,
                                      )}
                                    </span>
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    down from {formatNumber(impressionAvg, 0)}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-400">No drop</span>
                              )}
                            </div>

                            {/* Weekly Spend */}
                            <div className="col-span-2 text-center">
                              {costDrop > 0 ? (
                                <div>
                                  <div className="font-semibold text-red-700 flex items-center justify-center gap-1">
                                    <span>{costDrop.toFixed(0)}%</span>
                                    <span className="text-red-600">|</span>
                                    <span>
                                      $
                                      {formatNumber(
                                        costAvg * (costDrop / 100),
                                        0,
                                      )}
                                    </span>
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    down from ${formatNumber(costAvg, 0)}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="font-semibold text-slate-800">
                                    ${formatNumber(costAvg, 0)}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    no spend drop
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Conversions */}
                            <div className="col-span-2 text-center">
                              {conversionDrop > 0 ? (
                                <div>
                                  <div className="font-semibold text-red-700 flex items-center justify-center gap-1">
                                    <span>{conversionDrop.toFixed(0)}%</span>
                                    <span className="text-red-600">|</span>
                                    <span>
                                      {formatNumber(
                                        conversionAvg * (conversionDrop / 100),
                                        1,
                                      )}
                                    </span>
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    down from {formatNumber(conversionAvg, 1)}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-400">No drop</span>
                              )}
                            </div>

                            {/* Severity */}
                            <div className="col-span-2 text-center">
                              <Badge
                                variant={config.badge as any}
                                className="text-xs"
                              >
                                {config.label}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              {/* Show More Button */}
              {performanceInsights.campaigns.length >
                DEFAULT_VISIBLE_CAMPAIGNS && (
                <div className="text-center py-3 border-t">
                  <Button
                    variant="ghost"
                    onClick={() => setShowAllCampaigns(!showAllCampaigns)}
                    className="text-sm"
                  >
                    {showAllCampaigns
                      ? `Show Less`
                      : `See ${performanceInsights.campaigns.length - DEFAULT_VISIBLE_CAMPAIGNS} More Campaigns`}
                  </Button>
                </div>
              )}

              {/* Action Items */}
              <div className="pt-3 border-t bg-slate-50 -mx-4 px-4 py-3 rounded-b">
                <div className="text-sm text-slate-700">
                  <strong>Immediate actions:</strong> Check recent bid changes •
                  Review budget limits • Analyze competitor moves
                </div>
              </div>
            </div>
          </>
        ) : (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>Looking good!</strong> No significant performance drops
              detected. Your campaigns are performing consistently vs historical
              averages.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </QuickWinCard>
  );
};
